import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { mockData } from '../data/mock';
import { TrendingUp, Trophy, Target, Zap, GraduationCap, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const iconMap = {
  TrendingUp,
  Trophy,
  Target,
  Zap,
  GraduationCap
};

const Home = () => {
  const videoRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [mediaItems, setMediaItems] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch((err) => {
        console.log('Video autoplay prevented:', err);
      });
    }

    // Fetch media items from API
    const fetchMedia = async () => {
      try {
        const response = await fetch(`${API_URL}/api/media`);
        if (response.ok) {
          const data = await response.json();
          setMediaItems(data);
        }
      } catch (error) {
        console.log('Using fallback mock data');
        setMediaItems(mockData.mediaHub);
      }
    };
    fetchMedia();
  }, [API_URL]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster="/hero-video-poster.jpg"
            preload="auto">

            <source src={mockData.hero.videoUrl} type="video/mp4" />
          </video>
        </div>
      </section>

      {/* Programs Section */}
      <section className="programs-section grunge-texture">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Our Programs</h2>
          </div>
          <div className="programs-grid">
            {mockData.programs.map((program) =>
            <Card key={program.id} className="program-card">
                <div className="program-image-wrapper">
                  {program.image.endsWith('.mp4') ? (
                    <video 
                      src={program.image} 
                      poster={program.image.replace('.mp4', '-poster.jpg')}
                      className="program-image" 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                    />
                  ) : (
                    <img src={program.image} alt={program.title.replace('\n', ' ')} className="program-image" />
                  )}
                </div>
                <div className="program-content">
                  <h3 className="heading-4" style={{ whiteSpace: 'pre-line' }}>{program.title}</h3>
                  <p className="body-small">{program.description}</p>
                  <Button variant="link" className="link-text" asChild>
                    <Link to={program.link}>Learn More <ArrowRight size={16} /></Link>
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="impact-section grunge-texture">
        <div className="container">
          <div className="impact-header">
            <h2 className="heading-2 !text-[#FFFFFF]">Our Impact</h2>
          </div>
          <div className="impact-carousel-wrapper">
            <div className="impact-carousel-track">
              {/* Original cards */}
              {mockData.impact.map((metric) =>
                <div key={metric.id} className="impact-metric">
                  <div className="metric-number">{metric.number}</div>
                  <div className="metric-label">{metric.label}</div>
                </div>
              )}
              {/* Duplicated cards for seamless loop */}
              {mockData.impact.map((metric) =>
                <div key={`dup-${metric.id}`} className="impact-metric">
                  <div className="metric-number">{metric.number}</div>
                  <div className="metric-label">{metric.label}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="focus-areas-section grunge-texture">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Our Focus Areas</h2>
          </div>
          <div className="focus-areas-grid">
            {mockData.focusAreas.map((area) => {
              const IconComponent = iconMap[area.icon];
              return (
                <div key={area.id} className="focus-area-card">
                  <div className="focus-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="heading-5">{area.title}</h3>
                  <p className="body-small">{area.description}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section grunge-texture">
        <div className="container">
          <div className="partners-header">
            <p className="caption">Our Partners</p>
          </div>
        </div>
        <div className="partners-scroll-wrapper">
          <div className="partners-scroll-container">
            {/* Duplicate partners for seamless loop */}
            {[...mockData.partners, ...mockData.partners].map((partner, index) => (
              <div key={`${partner.id}-${index}`} className="partner-logo-item">
                <img src={partner.logo} alt={partner.name} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Hub */}
      <section className="media-hub-section grunge-texture">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Highlights & Mentions</h2>
            <div className="scroll-controls">
              <button onClick={scrollLeft} className="scroll-btn" aria-label="Scroll left">
                <ChevronRight size={24} style={{ transform: 'rotate(180deg)' }} />
              </button>
              <button onClick={scrollRight} className="scroll-btn" aria-label="Scroll right">
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
          <div className="media-scroll-container" ref={scrollContainerRef}>
            {(mediaItems.length > 0 ? mediaItems : mockData.mediaHub).map((item) =>
            <a key={item.id} href={item.external_link || item.url} className="media-card" target="_blank" rel="noopener noreferrer">
                <div className="media-image-wrapper">
                  <img 
                    src={item.image_url?.startsWith('/api') ? `${API_URL}${item.image_url}` : (item.image_url || item.image)} 
                    alt={item.title} 
                    className="media-image" 
                  />
                </div>
                <div className="media-content">
                  <h3 className="heading-6">{item.title}</h3>
                  <span className="caption">{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-content">
            <h2 className="heading-2">Nurture the Champions of Tomorrow</h2>
            <p className="body-large">Support the next generation of Indian athletes</p>
            <div className="cta-buttons">
              <Button className="btn-primary" asChild>
                <Link to="/contact">Get Involved</Link>
              </Button>
            </div>
          </div>
          <div className="cta-image-wrapper">
            <img src="/cta-team-photo.png" alt="Athletes" className="cta-image" />
          </div>
        </div>
      </section>
    </div>);

};

export default Home;